/* eslint-disable prettier/prettier */
import React from "react"
import { styled } from '@mui/material/styles'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import {
  Avatar,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Toolbar
} from '@mui/material'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import {
  ChevronLeft as ChevronLeftIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material'

import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'

import Settings from '@mui/icons-material/Settings'
import Logout from '@mui/icons-material/Logout'
import { usePurple } from '../PurpleContext'

import { useOktaAuth } from '@okta/okta-react'
import { PurpleMenuItem } from '../../data/interfaces'
import { RoleRequired } from '../RoleHelper'
import { CustomUserClaims, UserClaims } from "@okta/okta-auth-js"
const drawerWidth: number = 240

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}


const AppBar = styled(MuiAppBar, {})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9)
      }
    })
  }
}))

const NotificationsMenu = () => {
  const purple = usePurple()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const formatDate = (date: Date) => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ]
    return `${monthNames[date.getMonth()]} ${date.getDate()}`
  }

  return (
    <React.Fragment>
      <IconButton
        onClick={handleClick}
        color='inherit'
        sx={{ ml: 4 }}
        aria-controls={open ? 'notifications-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
      >
        <Badge badgeContent={4} color='secondary'>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id='notifications-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 1
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {purple.notifications.map((notification, index) => {
          return (
            <React.Fragment key={index}>
              <MenuItem
                sx={notification.unread ? { background: '#f5f5f5' } : {}}
              >
                <Grid container>
                  <Grid item>
                    <Typography
                      width={300}
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography sx={{ fontSize: '15px', color: '#8b8b8b' }}>
                      {notification.subTitle}
                    </Typography>
                  </Grid>
                  <Grid
                    sx={{
                      ml: 2,
                      pl: 2,
                      borderLeft: '1px solid #dbdbdb',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      display: 'flex'
                    }}
                  >
                    <Typography
                      color='primary'
                      fontWeight='bold'
                      fontSize='15px'
                    >
                      {formatDate(notification.date)} { }
                    </Typography>
                  </Grid>
                </Grid>
              </MenuItem>
              {index + 1 !== purple.notifications.length ? (
                <Divider sx={{ m: '0px !important' }} />
              ) : (
                <React.Fragment />
              )}
            </React.Fragment>
          )
        })}
      </Menu>
    </React.Fragment>
  )
}

const ProfileMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [avatarLetter, setAvatarLetter] = React.useState<string>('')
  const { oktaAuth, authState } = useOktaAuth()
  const { user } = usePurple()
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    oktaAuth.signOut()
  }

  React.useEffect(() => {
    if (authState?.isAuthenticated && !user) {
      oktaAuth.token
        .getUserInfo()
        .then((user: UserClaims<CustomUserClaims>) => {
          setAvatarLetter(user && user.name ? user.name[0] : '')
        })
    }
  }, [authState])

  return (
    <React.Fragment>
      <IconButton
        onClick={handleClick}
        size='small'
        sx={{ ml: 'auto' }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32 }}>{avatarLetter}</Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem href='/profile' component='a'>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem>
          <ListItemIcon>
            <Settings fontSize='small' />
          </ListItemIcon>
          Settings
        </MenuItem>
        {!authState?.isAuthenticated ? (
          <MenuItem>
            <ListItemIcon>
              <Logout fontSize='small' />
            </ListItemIcon>
            Login
          </MenuItem>
        ) : (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize='small' />
            </ListItemIcon>
            Logout
          </MenuItem>
        )}
      </Menu>
    </React.Fragment>
  )
}

const Topbar = ({ open, toggle }: { open: boolean; toggle(): void }) => {
  return (
    <AppBar position='absolute' open={open}>
      <Toolbar
        sx={{
          pr: '24px' // keep right padding when drawer closed
        }}
      >
        <IconButton
          edge='start'
          color='inherit'
          aria-label='open drawer'
          onClick={toggle}
          sx={{
            marginRight: '36px',
            ...(open && { display: 'none' })
          }}
        >
          <MenuIcon />
        </IconButton>
        <Typography component='h1' variant='h6' color='inherit' noWrap>
          Discount Engine
        </Typography>

        <NotificationsMenu />
        <ProfileMenu />
      </Toolbar>
    </AppBar>
  )
}

const Sidebar = ({ open, toggle }: { open: boolean; toggle(): void }) => {
  const purple = usePurple()

  const isSelected = (item: PurpleMenuItem) => {
    return window.location.pathname === item.link
  }

  const parentSelected = (item: PurpleMenuItem) => {

    if (!purple.navigation) {
      return false
    }

    const current = window.location.pathname

    const parent = purple.navigation.find((item) => {
      if (!item.children) {
        return false
      }

      return item.children.find((child) => {
        return child.link === current
      })
    })

    return item === parent
  }

  const SecondaryLevel = ({ item }: { item: PurpleMenuItem }) => {
    return (
      <ListItem
        button
        component='a'
        href={item.link}
        className={isSelected(item) ? 'selected' : ''}
      >
        <ListItemText
          primaryTypographyProps={{ fontSize: '15px' }}
          primary={item.title}
          sx={{ pl: 3, py: 0 }}
        />
      </ListItem>
    )
  }

  const MultiLevel = ({ item }: { item: PurpleMenuItem }) => {
    // const { items: children } = item;
    const [open, setOpen] = React.useState(parentSelected(item))

    const handleClick = () => {
      setOpen((prev) => !prev)
    }

    return (
      <React.Fragment>
        <ListItem button onClick={handleClick}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
        <Collapse in={open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {item.children.map((child, key) => (
              <SecondaryLevel key={key} item={child} />
            ))}
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  const SingleLevel = ({ item }: { item: PurpleMenuItem }) => {
    return (
      <ListItem
        button
        component='a'
        href={item.link}
        className={isSelected(item) ? 'selected' : ''}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.title} />
      </ListItem>
    )
  }

  const DisplayMenuItem = ({ item }: { item: PurpleMenuItem }) => {
    const hasChildren = (item: PurpleMenuItem) => {
      const { children } = item

      if (children === undefined) {
        return false
      }

      if (children.constructor !== Array) {
        return false
      }

      if (children.length === 0) {
        return false
      }

      return true
    }
    const Component = hasChildren(item) ? MultiLevel : SingleLevel
    return item.roles ? (
      <RoleRequired roles={item.roles ?? []}>
        <Component item={item} />
      </RoleRequired>
    ) : (
      <Component item={item} />
    )
  }

  return (
    <Drawer variant='permanent' open={open}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: [1]
        }}
      >
        <IconButton onClick={toggle}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      <List component='nav'>
        {(purple.navigation && purple.navigation.map((item, key) => (
          <DisplayMenuItem key={key} item={item} />
        )))}
      </List>
    </Drawer>
  )
}

const Navigation = () => {
  const [open, setOpen] = React.useState(true)
  const toggleDrawer = () => {
    setOpen(!open)
  }

  return (
    <React.Fragment>
      <Topbar open={open} toggle={toggleDrawer} />
      <Sidebar open={open} toggle={toggleDrawer} />
    </React.Fragment>
  )
}

export default Navigation;
